import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useGetRequestQuery } from '../../../modules/request/api/request-api';
import { requestPreviewAlertId } from '../../../modules/request/constants/constants';
import { selectActiveRequestId } from '../../../modules/request/store/requests-slice';
import { useDeleteScanRequestMutation } from '../../../modules/scan-requests/api/scan-requests-api';
import { scanRequestAlertId } from '../../../modules/scan-requests/constants/constants';
import { useDeleteSignRequestMutation } from '../../../modules/sign-requests/api/sign-requests-api';
import { signRequestAlertId } from '../../../modules/sign-requests/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../modules/ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../modules/ui/store/ui-slice';
import { useAppSelector } from '../../../store/hooks';
import { deleteAllDocumentsModalId } from '../../constants/constants';
import { useConfirmationError } from '../../hooks/use-confirmation-error';
import ModalConfirmationContent from '../modal-confirmation-content/modal-confirmation-content';
import Modal from '../modal/modal';
import { setIsSignRequestPreviewOpen } from '../../../modules/sign-requests/store/sign-requests-slice';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGoBack } from '../../hooks/use-go-back';
import { Routes } from '../../../app/routes';
import { setIsScanRequestPreviewOpen } from '../../../modules/scan-requests/store/scan-requests-slice';

interface Props {
  fallbackRoute?: Routes;
}

const DocumentsDeleteAllModalContent = ({
  isOpen,
  afterClose,
  fallbackRoute,
}: LazyModalComponentProps & Props): JSX.Element => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const activeRequestId = useAppSelector(selectActiveRequestId);
  const [deleteScanRequest, { isLoading: isLoadingDeleteScanRequest }] = useDeleteScanRequestMutation();
  const [deleteSinRequest, { isLoading: isLoadingDeleteSignRequest }] = useDeleteSignRequestMutation();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();
  const navigate = useNavigate();
  const goBackUrl = useGoBack(fallbackRoute);
  const location = useLocation();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { currentData: requestItem } = useGetRequestQuery(activeRequestId!, {
    skip: !activeRequestId,
  });

  const closeFormModal = () => {
    dispatch(closeModal(deleteAllDocumentsModalId));
    resetConfirmationError();
  };

  const setDeleteAllAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('documentsDeleteModal.successMessage.documentsDeleted'),
      }),
    );
  };

  const onDeleteSubmit = async () => {
    resetConfirmationError();
    if (requestItem) {
      const scanRequestIds = requestItem.scanRequests
        .filter(scanRequest => !scanRequest.deleted)
        .map(scanRequest => scanRequest.id);

      const signRequestsIds = requestItem.signRequests
        .filter(signRequest => !signRequest.deleted)
        .map(signRequest => signRequest.id);

      await Promise.all([
        ...scanRequestIds.map(scanRequestId => deleteScanRequest(scanRequestId).unwrap()),
        ...signRequestsIds.map(signRequestId => deleteSinRequest(signRequestId).unwrap()),
      ])
        .then(() => {
          setDeleteAllAlertMessage(scanRequestAlertId);
          setDeleteAllAlertMessage(signRequestAlertId);
          setDeleteAllAlertMessage(requestPreviewAlertId);

          if (activeRequestId && location.pathname.includes(activeRequestId)) {
            navigate(goBackUrl, { replace: true });
          } else {
            dispatch(setIsSignRequestPreviewOpen(false));
            dispatch(setIsScanRequestPreviewOpen(false));
          }

          closeFormModal();
        })
        .catch(() => addConfirmationError(t('errors.deleteFailed')));
    }
  };

  return (
    <Modal
      title={t('documentsDeleteModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoadingDeleteScanRequest || isLoadingDeleteSignRequest}
      customSubmitText={t('action.delete')}
      onOk={onDeleteSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <ModalConfirmationContent
        confirmDeleteText={t('documentsDeleteModal.hint.warningAll')}
        confirmationError={confirmationError}
      />
    </Modal>
  );
};

const DocumentsDeleteAllModal = (): JSX.Element => {
  return <LazyModal modalId={deleteAllDocumentsModalId} Component={DocumentsDeleteAllModalContent} />;
};

export default DocumentsDeleteAllModal;
